import * as React from "react";
import type { ComponentProps } from "react";
import { cn } from "app/javascript/utils/index";
import {
  Button,
  buttonVariants as buttonVariantsBase,
} from "app/javascript/components/ui/shadcn/button";

type ShadcnButtonVariant = NonNullable<ComponentProps<typeof Button>["variant"]>;
type BrandButtonVariant = "warning";
type AppButtonVariant = ShadcnButtonVariant | BrandButtonVariant;
type Size = NonNullable<ComponentProps<typeof Button>["size"]>;

export type AppButtonProps = Omit<ComponentProps<typeof Button>, "variant"> & {
  variant?: AppButtonVariant;
};

// 状態色の濃い塗り（design_system.md「状態色は次の組み合わせで使う」の「濃い塗り」）。
// 例: 警告の囲み（AppAlert variant="warning"）の中の操作ボタン
const brandVariantClassName: Record<BrandButtonVariant, string> = {
  warning: "bg-tt-warning text-tt-warning-foreground hover:bg-tt-warning/90",
};
const sizeOverrides: Partial<Record<Size, string>> = {
  default: "h-10 gap-1.5 px-3.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
  // 空状態など、次の操作へ誘導する大きめのボタン。default（h-10）と見分けがつくよう 48px にする
  lg: "h-12 gap-2 px-5.5",
};

// 型ガードで独自 variant と shadcn の variant を分け、キャスト無しで shadcn に渡す
const isBrandVariant = (variant: AppButtonVariant): variant is BrandButtonVariant => variant in brandVariantClassName;

// 独自の variant は shadcn の default を土台にして色を足す
function shadcnVariant(variant: AppButtonVariant): ShadcnButtonVariant {
  return isBrandVariant(variant) ? "default" : variant;
}

function overrideClassName(variant: AppButtonVariant, size: Size) {
  return cn(isBrandVariant(variant) ? brandVariantClassName[variant] : undefined, sizeOverrides[size]);
}

function AppButton({ className, variant, size, ...props }: AppButtonProps) {
  const resolvedVariant = variant ?? "default";
  const resolvedSize = size ?? "default";
  return (
    <Button
      variant={shadcnVariant(resolvedVariant)}
      size={resolvedSize}
      className={cn(overrideClassName(resolvedVariant, resolvedSize), className)}
      {...props}
    />
  );
}

type ButtonVariantsOptions = {
  variant?: AppButtonVariant;
  size?: Size;
  className?: string;
};

const buttonVariants = ({ variant = "default", size = "default", className }: ButtonVariantsOptions = {}) =>
  cn(buttonVariantsBase({ variant: shadcnVariant(variant), size, className }), overrideClassName(variant, size));

export { AppButton, buttonVariants };
